/**
 * src/shell/auth/rbac-gate.ts
 *
 * Role-Based Access Control (RBAC) & Fine-Grained Permission Evaluator.
 */

import { UserAccount, UserRole, Permission } from './auth-types';

export const ROLE_DEFAULT_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    'admin:all',
    'settings:cluster',
    'settings:network',
    'settings:security',
    'depin:lease',
    'depin:host',
    'apps:install',
    'apps:run_paid',
    'store:browse',
    'store:purchase',
    'doors:open_standard',
    'doors:open_secure',
    'finance:withdraw',
  ],
  developer: [
    'settings:network',
    'depin:lease',
    'depin:host',
    'apps:install',
    'apps:run_paid',
    'store:browse',
    'store:purchase',
    'doors:open_standard',
  ],
  operator: [
    'settings:cluster',
    'settings:network',
    'depin:host',
    'store:browse',
    'doors:open_standard',
  ],
  member: [
    'apps:install',
    'apps:run_paid',
    'store:browse',
    'store:purchase',
    'doors:open_standard',
  ],
  guest: [
    'store:browse',
  ],
};

export class RbacGate {
  /**
   * Checks if a user has a specific permission.
   * Admins with 'admin:all' inherently possess all permissions.
   */
  public static hasPermission(user: UserAccount | null | undefined, permission: Permission): boolean {
    if (!user) return false;
    if (user.role === 'admin' || user.permissions.includes('admin:all')) return true;
    return user.permissions.includes(permission);
  }

  /**
   * Checks if the user is authorized to access the SuperAdmin dashboard.
   */
  public static canAccessAdminDashboard(user: UserAccount | null | undefined): boolean {
    return this.hasPermission(user, 'admin:all') || user?.role === 'admin';
  }

  /**
   * Checks if the user has access to a specific settings tab or feature.
   */
  public static canAccessSettings(user: UserAccount | null | undefined, settingType: 'cluster' | 'network' | 'security'): boolean {
    const permMap: Record<'cluster' | 'network' | 'security', Permission> = {
      cluster: 'settings:cluster',
      network: 'settings:network',
      security: 'settings:security',
    };
    return this.hasPermission(user, permMap[settingType]);
  }

  /**
   * Checks if the user can unlock a physical door.
   */
  public static canUnlockDoor(user: UserAccount | null | undefined, doorTier: 'standard' | 'secure_server_room'): boolean {
    if (doorTier === 'secure_server_room') {
      return this.hasPermission(user, 'doors:open_secure');
    }
    return this.hasPermission(user, 'doors:open_standard');
  }
}
